import { useEffect, useState } from "react";
import {
  Sparkles, ChevronUp, ChevronDown, ThumbsUp, ThumbsDown,
} from "lucide-react";
import api from "../../lib/apiClient";
import { fmtCAD } from "../../lib/format";

/**
 * "Made for you" panel — customer intelligence header used on JobDetail.
 * Every number is fetched live from GET /api/customers/{cid}/summary.
 * Fields not yet tracked in the data model (ratings, service plans) render
 * as "no data" or are omitted, per the "never fake a number" contract.
 */
export function MadeForYouPanel({ customerId }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(true);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!customerId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data: d } = await api.get(`/customers/${customerId}/summary`);
        if (!cancelled) setData(d);
      } catch (e) {
        if (!cancelled) setErr(e?.response?.data?.detail || "Could not load customer summary");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [customerId]);

  const genAt = data?.generated_at ? new Date(data.generated_at) : null;
  const stamp = genAt ? genAt.toLocaleString("en-CA", { year: "numeric", month: "2-digit", day: "2-digit", hour: "numeric", minute: "2-digit", hour12: true }) : "";

  const historyPieces = data ? [
    `${data.prior_visits} prior visit${data.prior_visits === 1 ? "" : "s"}`,
    data.jobs_completed_unrated > 0 ? `${data.jobs_completed_unrated} job${data.jobs_completed_unrated === 1 ? "" : "s"} recently completed unrated` : null,
    (data.expired_estimates > 0 || data.open_estimates > 0)
      ? `${data.expired_estimates > 0 ? `${data.expired_estimates} expired estimate${data.expired_estimates === 1 ? "" : "s"} and ` : ""}${data.open_estimates} open estimate${data.open_estimates === 1 ? "" : "s"} on file.`
      : null,
  ].filter(Boolean) : [];

  const financialPieces = data ? [
    // Part 23 (Feb 2026) — Item 12: expose total-owed all-time and YTD paid
    // on the "Made for you" panel. These are the same numbers now surfaced
    // on the invoice header and PDF, computed live from payment records.
    `Total owed ${fmtCAD(data.total_owed ?? data.open_balance ?? 0)}`,
    `YTD paid${data.ytd_year ? ` (${data.ytd_year})` : ""} ${fmtCAD(data.ytd_paid ?? 0)}`,
    `LTV ${fmtCAD(data.ltv)}`,
    `${data.open_invoices_count} open invoice${data.open_invoices_count === 1 ? "" : "s"}`,
    data.active_service_plan ? `Service plan: ${data.active_service_plan}` : "No active service plan.",
  ] : [];

  return (
    <section
      data-testid="made-for-you-panel"
      className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-md"
    >
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full px-5 py-3 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between gap-3 bg-slate-50/50 dark:bg-slate-900"
        aria-expanded={open}
      >
        <div className="flex items-center gap-2">
          <Sparkles size={16} className="text-[#0F4C81]" />
          <h3 className="font-display font-semibold text-slate-900 dark:text-slate-100">Made for you</h3>
        </div>
        {open ? <ChevronUp size={18} className="text-slate-500" /> : <ChevronDown size={18} className="text-slate-500" />}
      </button>
      {open && (
        <div className="p-5 space-y-5" data-testid="made-for-you-body">
          {loading && <div className="text-sm text-slate-400">Loading customer summary…</div>}
          {err && <div className="text-sm text-red-600">{err}</div>}
          {!loading && !err && data && (
            <>
              <div>
                <div className="text-[11px] uppercase tracking-wider text-slate-500 dark:text-slate-400 font-medium mb-1">Customer history</div>
                <p className="text-sm text-slate-800 dark:text-slate-200" data-testid="mfy-history">
                  {historyPieces.length > 0 ? historyPieces.join(" • ") : "No prior activity."}
                </p>
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-wider text-slate-500 dark:text-slate-400 font-medium mb-1">Financials</div>
                <p className="text-sm text-slate-800 dark:text-slate-200" data-testid="mfy-financials">
                  {financialPieces.join(" • ")}
                </p>
              </div>
              <div className="flex items-center justify-between pt-2">
                <div className="flex items-center gap-2">
                  <button className="p-1.5 text-slate-400 hover:text-emerald-600" title="Helpful"><ThumbsUp size={14} /></button>
                  <button className="p-1.5 text-slate-400 hover:text-red-600" title="Not helpful"><ThumbsDown size={14} /></button>
                </div>
                {stamp && <div className="text-xs text-slate-400">Last updated {stamp}</div>}
              </div>
            </>
          )}
        </div>
      )}
    </section>
  );
}

/**
 * Sidebar widget stubs — REMOVED in Part 20 (Feb 2026).
 * Checklists / Tasks / Fields / Job tags / Private notes / Photo reports /
 * Lead source were placeholder cards. They were replaced with the real
 * SidebarAttachments widget on JobDetail. Private notes still lives at the
 * bottom of the main column. If any of these features come back, add them
 * as their own dedicated components.
 */
